import { LazyMotion, domAnimation, m } from "framer-motion";
import { Shield, CheckCircle, Building, Globe, Pill, Stethoscope } from "lucide-react";
import { Link } from "react-router-dom";
import heroVideo from "@/assets/hero-video.mp4";
import heroBgDesktop from "@/assets/hero-bg.jpg?format=webp&w=1920";
import heroBgMobile from "@/assets/hero-mobile-texture.jpg?format=webp";
import GemDiamond from "@/components/GemDiamond";
import GemIcon from "@/components/GemIcon";
import { useIsMobile } from "@/hooks/use-mobile";

const trustItems = [
  { icon: Shield, label: "Secure Architecture" },
  { icon: CheckCircle, label: "Compliance-First Design" },
  { icon: Building, label: "Centralized Operational Control" },
  { icon: Globe, label: "Built for Regulated Markets" },
];

const Hero = () => {
  const isMobile = useIsMobile();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden dark">
      {/* Eager hero background for LCP */}
      <picture className="absolute inset-0 w-full h-full">
        <source media="(max-width: 768px)" srcSet={heroBgMobile} />
        <source media="(min-width: 769px)" srcSet={heroBgDesktop} />
        <img
          src={heroBgDesktop}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover"
          fetchPriority="high"
          decoding="sync"
          width={1920}
          height={1080}
        />
      </picture>
      {!isMobile && (
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          className="absolute inset-0 w-full h-full object-cover hidden md:block"
        >
          <source src={heroVideo} type="video/mp4" />
        </video>
      )}

      <div className="absolute inset-0 bg-navy/80" />


      <LazyMotion features={domAnimation}>
        <div className="container mx-auto relative z-10 pt-24 pb-12 md:pt-40 md:pb-20 text-center">
          <m.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="max-w-3xl mx-auto"
          >
            <m.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-lime/10 text-lime text-xs font-semibold px-4 py-2 rounded-full mb-4 md:mb-6 border border-lime/20"
            >
              Growth Infrastructure for Pharmacies & Clinics
            </m.div>

            <h1 className="text-2xl md:text-5xl lg:text-6xl font-heading font-bold leading-[1.08] mb-3 md:mb-5">
              <span className="text-foreground">The Revenue Infrastructure</span>
              <br />
              <span className="text-gradient">That Scales Your Pharmacy.</span>
            </h1>

            <m.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-sm md:text-lg text-foreground mb-6 md:mb-8 max-w-xl mx-auto leading-relaxed"
            >
              Sullivan Care turns provider interest into long-term revenue — through digital onboarding that converts 3× more clinics, network management that retains accounts, and an enrollment pipeline engineered for measurable growth.
            </m.p>

            <m.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35 }}
              className="text-lime font-medium tracking-[0.25em] uppercase text-xs mb-3"
            >
              Select Your Organization
            </m.p>
            <m.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-row items-stretch justify-center gap-3 sm:gap-5"
            >
              {[
                { icon: Pill, label: "I'm a Pharmacy", desc: "Compounding, specialty & retail pharmacy solutions.", to: "/pharmacies" },
                { icon: Stethoscope, label: "I'm a Physician", desc: "Patient acquisition & growth systems for clinics.", to: "/physicians" },
              ].map((opt) => (
                <Link key={opt.label} to={opt.to}
                  className="provider-selector-card flex flex-col items-center justify-center w-1/2 sm:w-56 rounded-xl border-2 border-foreground/15 bg-foreground/5 backdrop-blur-sm px-4 py-5 sm:p-6 text-center transition-all duration-300 hover:bg-foreground/10 group"
                >
                  <div className="provider-selector-icon w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-lime/10 flex items-center justify-center mb-2 sm:mb-3 group-hover:bg-lime/20 transition-colors">
                    <GemIcon icon={opt.icon} size={22} />
                  </div>
                  <h3 className="font-heading font-bold text-sm sm:text-lg text-foreground mb-0.5 relative z-10">{opt.label}</h3>
                  <p className="text-muted-foreground text-[10px] sm:text-xs max-w-[180px] leading-snug hidden sm:block relative z-10">{opt.desc}</p>
                </Link>
              ))}
            </m.div>

            {/* Pricing Anchor */}
            <m.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-4 text-muted-foreground text-xs sm:text-sm"
            >
              Invest in your growth infrastructure for <span className="text-lime font-semibold">$1,500/mo</span> — designed to pay for itself through accelerated onboarding and higher retention.
            </m.p>

            {/* Trust Strip */}
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2"
            >
              {trustItems.map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-1.5 text-muted-foreground text-[10px] md:text-sm font-medium">
                  <GemIcon icon={Icon} size={14} />
                  {label}
                </span>
              ))}
            </m.div>
          </m.div>
        </div>
      </LazyMotion>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

export default Hero;
